import { useMemo } from 'react';
import { ChoiceDrill } from './ChoiceDrill';
import { makeMechRound, type Gear, type MechQuestion } from './mech';
import type { DrillProps } from './meta';

/** 시계(또는 반시계) 방향 화살표 — 기어 위쪽에 호로 그린다 */
function Spin({ x, y, r, cw }: { x: number; y: number; r: number; cw: boolean }) {
  const a0 = cw ? -150 : -30, a1 = cw ? -30 : -150;
  const p = (a: number) => [x + (r + 7) * Math.cos((a * Math.PI) / 180), y + (r + 7) * Math.sin((a * Math.PI) / 180)];
  const [x0, y0] = p(a0), [x1, y1] = p(a1);
  return <path d={`M${x0},${y0} A${r + 7},${r + 7} 0 0 ${cw ? 1 : 0} ${x1},${y1}`} className="mech-arrow" markerEnd="url(#mech-ah)" />;
}

function Gears({ q }: { q: Extract<MechQuestion, { kind: 'gears' }> }) {
  const pad = 14;
  const xs = q.gears.flatMap((g) => [g.x - g.r, g.x + g.r]), ys = q.gears.flatMap((g) => [g.y - g.r, g.y + g.r]);
  const minX = Math.min(...xs) - pad, minY = Math.min(...ys) - pad - 8, w = Math.max(...xs) - minX + pad, h = Math.max(...ys) - minY + pad;
  const onShaft = new Set(q.shafts.flat());
  // 같은 축 기어는 큰 것을 먼저 그려 작은 것이 위에 보이게
  const order = [...q.gears].sort((a, b) => (onShaft.has(q.gears.indexOf(b)) ? 0 : 1) - (onShaft.has(q.gears.indexOf(a)) ? 0 : 1) || b.r - a.r);
  return (
    <svg className="mech" viewBox={`${minX} ${minY} ${w} ${h}`} role="img" aria-label="기어 그림">
      <MechDefs />
      {order.map((g: Gear) => (
        <g key={g.label}>
          <circle cx={g.x} cy={g.y} r={g.r} className="gear" style={{ strokeDasharray: `${((2 * Math.PI * g.r) / g.teeth / 2).toFixed(2)}` }} />
          <circle cx={g.x} cy={g.y} r={g.r - 4} className="gear-body" />
          <circle cx={g.x} cy={g.y} r="2.5" className="gear-axle" />
          <text x={g.x} y={g.y - (onShaft.has(q.gears.indexOf(g)) && g.r < 14 ? 0 : 5)} textAnchor="middle" className="mech-label">{g.label}</text>
          {q.showTeeth && <text x={g.x} y={g.y + (g.r > 14 ? 9 : 4)} textAnchor="middle" className="mech-small">{g.teeth}개</text>}
        </g>
      ))}
      <Spin x={q.gears[0].x} y={q.gears[0].y} r={q.gears[0].r} cw={q.driverCW} />
    </svg>
  );
}

function Belt({ q }: { q: Extract<MechQuestion, { kind: 'belt' }> }) {
  const ra = 8 + q.ra * 0.8, rb = 8 + q.rb * 0.8, xa = ra + 12, xb = xa + ra + rb + 60, y = Math.max(ra, rb) + 16;
  const lines = q.crossed
    ? [[xa, y - ra, xb, y + rb], [xa, y + ra, xb, y - rb]]
    : [[xa, y - ra, xb, y - rb], [xa, y + ra, xb, y + rb]];
  return (
    <svg className="mech" viewBox={`0 0 ${xb + rb + 12} ${2 * y}`} role="img" aria-label={q.crossed ? 'X자로 꼰 벨트' : '그냥 건 벨트'}>
      <MechDefs />
      {lines.map(([a, b, c, d], i) => <line key={i} x1={a} y1={b} x2={c} y2={d} className="belt" />)}
      <circle cx={xa} cy={y} r={ra} className="gear-body" />
      <circle cx={xb} cy={y} r={rb} className="gear-body" />
      <text x={xa} y={y + 4} textAnchor="middle" className="mech-label">A</text>
      <text x={xb} y={y + 4} textAnchor="middle" className="mech-label">B</text>
      <Spin x={xa} y={y} r={ra} cw />
    </svg>
  );
}

function Lever({ q }: { q: Extract<MechQuestion, { kind: 'lever' }> }) {
  const step = 24, cx = 150, by = 70;
  return (
    <svg className="mech" viewBox="0 0 300 100" role="img" aria-label="지렛대">
      <line x1={cx - 5.5 * step} y1={by} x2={cx + 5.5 * step} y2={by} className="beam" />
      <polygon points={`${cx},${by + 2} ${cx - 12},${by + 24} ${cx + 12},${by + 24}`} className="fulcrum" />
      {[-5, -4, -3, -2, -1, 1, 2, 3, 4, 5].map((p) => <line key={p} x1={cx + p * step} y1={by - 3} x2={cx + p * step} y2={by + 3} className="tick" />)}
      {q.weights.map((w) => (
        <g key={w.pos}>
          <rect x={cx + w.pos * step - 13} y={by - 28} width="26" height="24" rx="3" className={w.kg === null ? 'box ask' : 'box'} />
          <text x={cx + w.pos * step} y={by - 12} textAnchor="middle" className="mech-small">{w.kg === null ? '?' : `${w.kg}kg`}</text>
        </g>
      ))}
    </svg>
  );
}

function Pulley({ q }: { q: Extract<MechQuestion, { kind: 'pulley' }> }) {
  const n = q.ropes, top = 22, bottom = 108, gap = 14;
  const x0 = 110 - ((n - 1) * gap) / 2;
  const xs = Array.from({ length: n }, (_, i) => x0 + i * gap);
  const free = xs[n - 1] + 26;
  return (
    <svg className="mech" viewBox="0 0 240 170" role="img" aria-label={`도르래, 물체를 받치는 줄이 그림에 보인다`}>
      <MechDefs />
      <rect x="40" y="6" width="160" height="8" className="ceiling" />
      <rect x={x0 - 12} y={14} width={(n - 1) * gap + 24 + 26} height={14} rx="4" className="block" />
      <circle cx={(xs[n - 1] + free) / 2} cy={top} r="7" className="gear-body" />
      {xs.map((x) => <line key={x} x1={x} y1={top + 6} x2={x} y2={n === 1 ? bottom + 8 : bottom} className="rope" />)}
      <line x1={free} y1={top} x2={free} y2={150} className="rope" markerEnd="url(#mech-ah)" />
      <text x={free + 6} y={150} className="mech-small">F</text>
      {n > 1 && <rect x={x0 - 12} y={bottom} width={(n - 1) * gap + 24} height={12} rx="4" className="block" />}
      <rect x={110 - 24} y={n > 1 ? bottom + 12 : bottom + 8} width="48" height="30" rx="3" className="box" />
      <text x="110" y={n > 1 ? bottom + 32 : bottom + 28} textAnchor="middle" className="mech-small">{q.kg}kg</text>
    </svg>
  );
}

function Incline({ q }: { q: Extract<MechQuestion, { kind: 'incline' }> }) {
  const W = 240, H = 30 + q.h * 30;
  const run = Math.sqrt(Math.max(1, (q.len * 40) ** 2 - (q.h * 30) ** 2));
  return (
    <svg className="mech" viewBox={`0 0 ${W + 60} ${H + 40}`} role="img" aria-label="빗면">
      <polygon points={`20,${H + 20} ${20 + run},${H + 20} ${20 + run},20`} className="slope" />
      <text x={30 + run} y={H / 2 + 24} className="mech-small">높이 {q.h}m</text>
      <text x={10 + run / 2 - 30} y={H / 2 + 10} className="mech-small">빗면 {q.len}m</text>
      <rect x={20 + run * 0.45 - 12} y={20 + H * 0.55 - 22} width="24" height="18" className="box" transform={`rotate(${(-Math.atan2(H, run) * 180) / Math.PI} ${20 + run * 0.45} ${20 + H * 0.55})`} />
    </svg>
  );
}

function Hydraulic({ q }: { q: Extract<MechQuestion, { kind: 'hydraulic' }> }) {
  return (
    <svg className="mech" viewBox="0 0 280 150" role="img" aria-label="유압 장치">
      <MechDefs />
      <path d="M40,40 L40,120 L240,120 L240,40 L170,40 L170,100 L70,100 L70,40 Z" className="fluid" />
      <rect x="36" y="44" width="38" height="8" className="block" />
      <rect x="166" y="44" width="78" height="8" className="block" />
      <line x1="55" y1="8" x2="55" y2="40" className="mech-arrow" markerEnd="url(#mech-ah)" />
      <line x1="205" y1="40" x2="205" y2="10" className="mech-arrow" markerEnd="url(#mech-ah)" />
      <text x="62" y="20" className="mech-small">{q.force}N</text>
      <text x="212" y="22" className="mech-small">?</text>
      <text x="30" y="140" className="mech-small">넓이 {q.small}cm²</text>
      <text x="170" y="140" className="mech-small">넓이 {q.big}cm²</text>
    </svg>
  );
}

function MechDefs() {
  return (
    <defs>
      <marker id="mech-ah" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
        <path d="M0,0 L10,5 L0,10 z" className="mech-arrowhead" />
      </marker>
    </defs>
  );
}

export function MechDrill({ level, seed, onDone }: DrillProps) {
  const questions = useMemo(() => makeMechRound(level, seed), [level, seed]);
  return (
    <ChoiceDrill
      questions={questions} seed={seed} onDone={onDone} className="mech-drill"
      info={(q) => ({ kind: q.kind, ask: q.ask })}
      choicesClass={(q) => (q.options.some((o) => o.length > 12) ? 'one-col' : '')}
      render={(q) => (
        <div className="mech-q">
          {q.kind === 'gears' && <Gears q={q} />}
          {q.kind === 'belt' && <Belt q={q} />}
          {q.kind === 'lever' && <Lever q={q} />}
          {q.kind === 'pulley' && <Pulley q={q} />}
          {q.kind === 'incline' && <Incline q={q} />}
          {q.kind === 'hydraulic' && <Hydraulic q={q} />}
          <p className="lang-ask small-ask">{q.ask}</p>
        </div>
      )}
      renderChoice={(q, i) => <span className="choice-text">{q.options[i]}</span>}
    />
  );
}
